/**
 * 通常のボタン
 */

import { ReactNode } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';

type ButtonSize = 'xs' | 's' | 'm' | 'l';
type ButtonVariant =
  | 'primary'
  | 'tenant_primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'blue_quaternary';

type Props = {
  href?: string;
  target?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
  outline?: boolean;
  fullWidth?: boolean;
  fullRounded?: boolean;
  children: ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  loading?: boolean;
  'data-testid'?: string;
};

const Button = ({
  href,
  target,
  size = 'm',
  variant = 'primary',
  outline = false,
  fullWidth = false,
  fullRounded = false,
  children,
  onClick,
  disabled = false,
  type = 'button',
  className,
  loading,
  ...restProps
}: Props) => {
  const baseClassName =
    'block transition-opacity hover:opacity-60 disabled:opacity-100 disabled:cursor-not-allowed';

  const sizeClasses = {
    xs: 'px-4 py-[8px] text-sm',
    s: 'px-6 py-[10px] text-sm',
    m: 'px-6 py-3 text-base',
    l: 'px-7 py-6 text-xl',
  };

  const loadingSize = {
    xs: 12,
    s: 16,
    m: 18,
    l: 20,
  };

  // 塗りの色（outline = false）
  const variantClasses = {
    primary:
      'bg-primary-500 text-white border border-primary-500 border-solid disabled:bg-gray-200 disabled:border-gray-200',
    tenant_primary:
      'bg-primary-800 text-white border border-primary-800 border-solid disabled:bg-gray-200 disabled:border-gray-200',
    secondary:
      'bg-warning-500 text-white border border-warning-500 border-solid disabled:bg-gray-200 disabled:border-gray-200',
    tertiary:
      'bg-black text-white border border-black border-solid disabled:bg-gray-200 disabled:border-gray-200',
    quaternary:
      'bg-gray-100 text-black border border-gray-100 border-solid disabled:bg-gray-200 disabled:border-gray-200',
    blue_quaternary:
      'bg-gray-100 text-black border border-gray-100 border-solid disabled:bg-gray-200 disabled:border-gray-200',
  };
  // 枠線の色（outline = true）
  const variantOutlineClasses = {
    primary:
      'bg-white border border-primary border-solid text-primary-500 disabled:bg-gray-200 disabled:border-gray-200',
    tenant_primary:
      'bg-white border border-primary border-solid text-primary-500 disabled:bg-gray-200 disabled:border-gray-200',
    secondary:
      'bg-white border border-warning border-solid text-warning-500 disabled:bg-gray-200 disabled:border-gray-200',
    tertiary:
      'bg-white border border-black border-solid text-black disabled:bg-gray-200 disabled:border-gray-200',
    quaternary:
      'bg-white border border-gray-200 border-solid text-black disabled:bg-gray-200 disabled:border-gray-200',
    blue_quaternary:
      'bg-white border border-gray-200 border-solid text-primary-500 disabled:bg-gray-200 disabled:border-gray-200',
  };

  const fullWidthClass = fullWidth ? 'w-full' : '';
  const roundedClass = fullRounded ? 'rounded-full' : 'rounded-[4px]';

  const buttonClassName = `
    ${baseClassName}
    ${sizeClasses[size]}
    ${outline ? variantOutlineClasses[variant] : variantClasses[variant]}
    ${fullWidthClass}
    ${roundedClass}
    ${className}
  `.trim();

  if (href) {
    return (
      <a
        href={href}
        target={target || undefined}
        onClick={onClick}
        className={buttonClassName}
        {...restProps}
      >
        {children}
      </a>
    );
  } else {
    return (
      <button
        className={buttonClassName}
        onClick={onClick}
        disabled={loading || disabled}
        type={type}
        {...restProps}
      >
        {loading ? (
          <ClipLoader
            size={loadingSize[size]}
            className='!border-primary !border-b-transparent'
            loading={loading}
          />
        ) : (
          children
        )}
      </button>
    );
  }
};

export default Button;
